import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import {
  ApplicationState,
  CaseSettingsState,
  AppState,
  CaseSettingsActionCreators
} from '../../store';
import { withSnackbar, ProviderContext } from 'notistack';
import { AppContext, ApplicationContext } from '../../context/Contexts';
import { ITranslatorService } from '../../services/Interfaces/ITranslatorService';
import { RouteComponentProps } from 'react-router';
import { ScaleLoader } from 'react-spinners';
import { WorkflowSectionStepForm, WorkflowSectionStep } from '../../interfaces/Workflow';
import { isNullOrUndefined } from 'util';
import { ICaseService } from '../../services/Interfaces/ICaseService';
import { FormHelpers } from '../../helpers/forms/FormHelpers';
import 'react-dropdown-tree-select/dist/styles.css';
import { Button, Typography } from '@material-ui/core';
import { AppUser } from '../../interfaces/AppUser';
import {
  AddInvoiceCaseAttachmentResponseDto,
  CaseAttachment,
  ChangeCaseStatusRequest,
  GetCaseAttachmentsRequestDto,
  InvoiceResponseDto
} from '../../interfaces/Case';
import { IReferentialService } from '../../services/Interfaces/IReferentialService';
import { IReferential } from '../../interfaces/IReferential';
import {
  AttachmentTypeCode,
  CaseStatusCodeEnum,
  InvoiceTypeEnum,
  ParametreCode,
  ReferentialCode,
  WorkflowStepCodeEnum
} from '../../helpers/Constants';
import { OrganizationInfoDto } from '../../interfaces/Organization';
import { IOrganizationService } from '../../services/Interfaces/IOrganizationService';
import RepairServiceInvoiceModal from '../_shared/RepairServiceInvoiceModal';
import CaseAttachmentCard from '../_shared/CaseAttachmentCard';
import Referentials from '../../helpers/Referentials.json';
import repairFinishedImage from '../../assets/images/illustrations/repair_finished.png';
import plusSignImage from '../../assets/images/illustrations/plus_sign.png';
import checkmarkImage from '../../assets/images/illustrations/checkmark.png';
import updateCalc from '../../assets/images/illustrations/update_calc.png';

interface ICaseDetailsCaseRepairInProgressFormState {
  isLoading: boolean;
  hasRights: boolean;
  executing: boolean;
  caseStatuses: IReferential[];
  repairServiceInvoiceToGTM: InvoiceResponseDto | null;
  repairServiceInvoiceAttachments: CaseAttachment[];
  isRepairServiceInvoiceModalOpen: boolean;
  invoiceServiceOrganization: OrganizationInfoDto | null;
  repairServiceInvoiceAttachmentType: IReferential | null;
}

export interface ExternalCaseDetailsCaseRepairInProgressFormProps {
  workflowForm: WorkflowSectionStepForm;
}

type ICaseDetailsCaseRepairInProgressFormProps =
  ExternalCaseDetailsCaseRepairInProgressFormProps & {
    caseSettingsState: CaseSettingsState;
    appState: AppState;
  } & typeof CaseSettingsActionCreators &
    ProviderContext &
    RouteComponentProps<{ id: string }>;

class CaseDetailsCaseRepairInProgressForm extends React.PureComponent<
  ICaseDetailsCaseRepairInProgressFormProps,
  ICaseDetailsCaseRepairInProgressFormState
> {
  private translatorService!: ITranslatorService;
  private caseService!: ICaseService;
  private appReferentialService!: IReferentialService;
  private organizationService!: IOrganizationService;

  static contextType = ApplicationContext;
  state = {
    isLoading: false,
    hasRights: false,
    isUserHidden: false,
    executing: false,
    caseStatuses: [],
    repairServiceInvoiceToGTM: null,
    repairServiceInvoiceAttachments: [],
    isRepairServiceInvoiceModalOpen: false,
    invoiceServiceOrganization: null,
    repairServiceInvoiceAttachmentType: null
  } as ICaseDetailsCaseRepairInProgressFormState;

  public componentDidMount() {
    const caseSettings = this.props.caseSettingsState.caseSettings;
    const caseId = Number.parseInt(this.props.match.params.id);
    if (isNullOrUndefined(caseSettings) || Number.isNaN(caseId)) {
      return;
    }

    this.setState(
      {
        isLoading: true
      },
      async () => {
        await this.loadData();
      }
    );
  }

  loadData = async () => {
    const caseId = Number.parseInt(this.props.match.params.id);
    if (Number.isNaN(caseId)) {
      return;
    }

    const refCaseStatus = Referentials.referential.find(
      (item) => item.code === ReferentialCode.CaseStatus
    );
    const refAttachmentType = Referentials.referential.find(
      (item) => item.code === ReferentialCode.AttachmentType
    );
    const isInvoicedByGTM = this.props.caseSettingsState.case!.isInvoicedByGTM;
    const serviceId =
      this.props.caseSettingsState.case?.serviceId ?? this.props.appState.appUser!.organizationId!;

    const [
      caseStatuses,
      repairServiceInvoiceToGTM,
      repairServiceInvoiceAttachments,
      attachmentTypes,
      serviceOrganizations
    ] = await Promise.all([
      this.appReferentialService.Get(refCaseStatus!.baseUrl),
      isInvoicedByGTM
        ? this.caseService.GetLastInvoice({
            caseId: caseId,
            invoiceType: InvoiceTypeEnum.REPAIR_SERVICE_INVOICE_TO_GTM
          })
        : Promise.resolve(null),
      isInvoicedByGTM
        ? this.caseService.GetCaseAttachments({
            caseId: caseId,
            types: [AttachmentTypeCode.REPAIR_SERVICE_INVOICE_TO_GTM.toString()],
            viewHiddenServiceDocuments:
              this.props.appState.appUser!.organization!.organizationType!
                .viewHiddenServiceDocuments ?? false
          } as GetCaseAttachmentsRequestDto)
        : Promise.resolve([] as CaseAttachment[]),
      isInvoicedByGTM
        ? this.appReferentialService.Get(refAttachmentType!.baseUrl)
        : Promise.resolve([] as IReferential[]),
      isInvoicedByGTM
        ? this.organizationService.GetOrganizationByIds({ organizationIds: [serviceId] })
        : Promise.resolve([] as OrganizationInfoDto[])
    ]);

    const invoiceAttachments = repairServiceInvoiceAttachments.filter(
      (attach) =>
        attach.attachmentType?.code ===
          AttachmentTypeCode.REPAIR_SERVICE_INVOICE_TO_GTM.toString() && attach.invoiceId
    );
    const invoices = await Promise.all(
      invoiceAttachments.map((attach) => this.caseService.GetInvoiceById(attach.invoiceId!))
    );
    invoiceAttachments.forEach((attach) => {
      attach.invoice = invoices.find((invoice) => invoice?.id === attach.invoiceId) ?? null;
    });

    const repairServiceInvoiceAttachmentType =
      attachmentTypes.find(
        (item) => item.code === AttachmentTypeCode.REPAIR_SERVICE_INVOICE_TO_GTM.toString()
      ) ?? null;

    const serviceOrganization = serviceOrganizations[0] ?? null;
    let invoiceServiceOrganization = serviceOrganization;
    if (serviceOrganization?.hoIdIssuesInvoice && serviceOrganization.hoId) {
      invoiceServiceOrganization =
        (
          await this.organizationService.GetOrganizationByIds({
            organizationIds: [serviceOrganization.hoId]
          })
        )[0] ?? null;
    }

    const hasRights = FormHelpers.HasRights(
      this.props.workflowForm.workflowFormPermissions,
      this.props.appState.appUser!,
      this.props.appState,
      this.props.caseSettingsState.case!.caseStatus.caseStatusId
    );

    this.setState({
      isLoading: false,
      caseStatuses: caseStatuses,
      hasRights: hasRights,
      repairServiceInvoiceToGTM: repairServiceInvoiceToGTM,
      repairServiceInvoiceAttachments: repairServiceInvoiceAttachments,
      invoiceServiceOrganization: invoiceServiceOrganization,
      repairServiceInvoiceAttachmentType: repairServiceInvoiceAttachmentType
    });
  };

  repairHasBeenFinished = async (e: any) => {
    if (this.isRepairFinishedDisabled()) {
      this.setState({ isRepairServiceInvoiceModalOpen: true });
      return;
    }

    await this.finishRepair();
  };

  finishRepair = async () => {
    try {
      this.setState({ executing: true });

      const skipServiceDocuments =
        this.props.caseSettingsState.case!.isOffline ||
        this.props.caseSettingsState.case!.skipGeneratingDocuments;

      if (skipServiceDocuments) {
        await this.closeCase();
      } else {
        await this.submitForm(
          this.props.caseSettingsState.case!.id,
          this.props.workflowForm.id,
          null
        );
      }

      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });

      this.setState({ executing: false });
    } catch (ex) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    } finally {
      this.setState({ executing: false });
    }
  };

  closeRepairServiceInvoiceModal = () => {
    this.setState({ isRepairServiceInvoiceModalOpen: false });
  };

  handleRepairServiceInvoiceSaved = async (response: AddInvoiceCaseAttachmentResponseDto) => {
    const attachments = (response.caseAttachments ?? []).map(
      (attachment) => ({ ...attachment, invoice: response.invoice } as CaseAttachment)
    );
    this.setState({
      repairServiceInvoiceToGTM: response.invoice,
      repairServiceInvoiceAttachments: attachments
    });

    await this.finishRepair();
  };

  private renderRepairServiceInvoiceModal = () => {
    if (!this.state.repairServiceInvoiceAttachmentType) {
      return null;
    }

    const caseParameters = this.props.caseSettingsState.case?.caseParameters ?? [];
    const vatParameter = caseParameters.find(
      (parameter) => parameter.parameter!.code === ParametreCode.TVA
    );
    const vatValue = vatParameter ? parseFloat(vatParameter.value) : (null as any);
    const currencyParameter = caseParameters.find(
      (parameter) => parameter.parameter!.code === ParametreCode.CURRENCY
    );
    const currency = currencyParameter ? currencyParameter.value : '';

    return (
      <RepairServiceInvoiceModal
        open={this.state.isRepairServiceInvoiceModalOpen}
        caseId={this.props.caseSettingsState.case?.id ?? 0}
        serviceOrganization={this.state.invoiceServiceOrganization}
        attachmentTypeId={this.state.repairServiceInvoiceAttachmentType.id}
        attachmentTypeCode={this.state.repairServiceInvoiceAttachmentType.code ?? ''}
        vatValue={vatValue}
        currency={currency}
        onClose={this.closeRepairServiceInvoiceModal}
        onSaved={this.handleRepairServiceInvoiceSaved}
      />
    );
  };

  private renderRepairServiceInvoice = () => {
    if (
      !this.props.caseSettingsState.case!.isInvoicedByGTM ||
      !this.state.repairServiceInvoiceToGTM
    ) {
      return null;
    }

    return (
      <div
        className="d-flex flex-row flex-wrap justify-content-center mt-3"
        style={{ width: '100%' }}
      >
        <div className="text-center" style={{ width: '45%', margin: '0 16px' }}>
          <CaseAttachmentCard
            caseId={this.props.caseSettingsState.case?.id ?? 0}
            attachmentType={this.state.repairServiceInvoiceAttachmentType}
            attachments={this.state.repairServiceInvoiceAttachments}
            hasRights={this.state.hasRights}
            isMultiple={false}
            isSelectionGroup={false}
            allowOnlyImages={false}
            addWatermark={false}
            showUploadMessage={this.props.appState.appUser!.organization!.uploadFeature}
            downloadModelFile={false}
            viewHiddenServiceDocuments={false}
            organizationName={this.props.appState.appUser?.organization?.name ?? ''}
            organizationCity={this.props.appState.appUser?.organization?.cityInfo?.displayName ?? ''}
            onAttachmentsChanged={(attachments) =>
              this.setState({
                repairServiceInvoiceAttachments: attachments,
                repairServiceInvoiceToGTM: attachments[0]?.invoice ?? null
              })
            }
            onAddInvoice={() => this.setState({ isRepairServiceInvoiceModalOpen: true })}
            headerAction={null}
            isDamagePhoto={false}
          />
        </div>
        <div style={{ width: '45%', margin: '0 16px' }} />
      </div>
    );
  };

  closeCase = async () => {
    const handedOverStatus = this.state.caseStatuses.find(
      (x) => x.code === CaseStatusCodeEnum.MASINA_PREDATA.toString()
    );
    const caseStatusRequest = {
      caseId: this.props.caseSettingsState.case!.id,
      statusId: handedOverStatus!.id
    } as ChangeCaseStatusRequest;
    const currentStatus = await this.caseService.ChangeCaseStatus(caseStatusRequest);

    this.props.SetCaseStatus(currentStatus);

    const hasRights = FormHelpers.HasRights(
      this.props.workflowForm.workflowFormPermissions,
      this.props.appState.appUser!,
      this.props.appState,
      currentStatus.caseStatusId
    );
    this.setState({ hasRights: hasRights });
  };

  submitForm = async (caseId: number, caseSectionStepFormId: number, appUser: AppUser | null) => {
    let newStatus = await this.caseService.SubmitForm(caseId, caseSectionStepFormId, appUser);

    if (newStatus === null) {
      newStatus = this.props.caseSettingsState.case!.caseStatus;
    }

    this.props.SetCaseStatus(newStatus);
    if (appUser !== null) {
      this.props.AddPartner(appUser!.hoId === null ? appUser!.organizationId : appUser!.hoId);
    }

    const hasRights = FormHelpers.HasRights(
      this.props.workflowForm.workflowFormPermissions,
      this.props.appState.appUser!,
      this.props.appState,
      newStatus.caseStatusId
    );
    this.setState({ hasRights: hasRights });
  };

  updateOffer = async (e: any) => {
    try {
      this.setState({ executing: true });

      const newStatus = this.state.caseStatuses.find(
        (x) => x.code === CaseStatusCodeEnum.ACTUALIZEAZA_CALCULATIE.toString()
      );
      const caseStatusRequest = {
        caseId: this.props.caseSettingsState.case!.id,
        statusId: newStatus!.id
      } as ChangeCaseStatusRequest;
      const currentStatus = await this.caseService.ChangeCaseStatus(caseStatusRequest);

      this.props.SetCaseStatus(currentStatus);

      this.setState({ executing: false });
    } catch (ex) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    } finally {
      this.setState({ executing: false });
    }
  };

  getWorkflowSectionStep = (workflowSectionStepId: number): WorkflowSectionStep | null => {
    const sections = this.props.caseSettingsState.caseSettings?.workflow?.workflowSections;
    if (!sections) {
      return null;
    }
    for (const section of sections) {
      for (const step of section.workflowSectionSteps) {
        if (step.id === workflowSectionStepId) {
          return step;
        }
      }
    }
    return null;
  };

  private isRepairFinishedDisabled = (): boolean => {
    return (
      this.props.caseSettingsState.case!.isInvoicedByGTM && !this.state.repairServiceInvoiceToGTM
    );
  };

  private submitDefaultForm = async (e: any) => {
    try {
      this.setState({ executing: true });

      await this.submitForm(
        this.props.caseSettingsState.case!.id,
        this.props.workflowForm.id,
        null
      );

      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });

      this.setState({ executing: false });
    } catch (ex) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    } finally {
      this.setState({ executing: false });
    }
  };

  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.caseService = (this.context as AppContext).caseService;
    this.appReferentialService = (this.context as AppContext).referentialService;
    this.organizationService = (this.context as AppContext).organizationService;

    const isCarfixIntegration =
      this.props.caseSettingsState.caseSettings?.workflow?.isCarfixIntegration || false;
    const workflowSectionStep = this.getWorkflowSectionStep(
      this.props.workflowForm.workflowSectionStepId
    );
    const isGoToCalculationStep =
      workflowSectionStep?.workflowStep?.code === WorkflowStepCodeEnum.GO_TO_CALCULATION;
    return (
      <Fragment>
        <div className="d-flex flex-row text-center flex-wrap justify-content-center">
          <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
        </div>
        {!this.state.isLoading
          ? isCarfixIntegration && !isGoToCalculationStep
            ? this.renderCaseRepairInProgress()
            : this.renderDefaultButtons()
          : null}
        {this.renderRepairServiceInvoiceModal()}
      </Fragment>
    );
  }

  private renderCaseRepairInProgress = () => {
    const isDisabled = !this.state.hasRights || this.state.executing;
    const disabledColor = '#a0a0a0';

    const baseCardStyle: React.CSSProperties = {
      borderRadius: '8px',
      padding: '32px',
      margin: '16px',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '45%',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      backgroundColor: '#ffffff'
    };

    const firstCardStyle: React.CSSProperties = {
      ...baseCardStyle,
      border: isDisabled ? `1px solid ${disabledColor}` : '1px solid #606060'
    };

    const secondCardStyle: React.CSSProperties = {
      ...baseCardStyle,
      border: isDisabled ? `2px solid ${disabledColor}` : '2px solid #4caf50'
    };

    const containerIconStyle: React.CSSProperties = {
      position: 'absolute',
      top: '16px',
      right: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    };

    const iconImageStyle: React.CSSProperties = {
      width: '70%',
      height: 'auto',
      objectFit: 'contain'
    };

    const imageStyle: React.CSSProperties = {
      width: '90%',
      height: 'auto',
      objectFit: 'contain',
      marginBottom: '20px',
      marginTop: '30px'
    };

    const firstButtonStyle: React.CSSProperties = {
      backgroundColor: '#ffffff',
      color: isDisabled ? disabledColor : '#606060',
      border: `1px solid ${isDisabled ? disabledColor : '#606060'}`,
      textTransform: 'none',
      padding: '10px 20px',
      marginTop: '8px'
    };

    const secondButtonStyle: React.CSSProperties = {
      backgroundColor: isDisabled ? disabledColor : '#4caf50',
      color: '#ffffff',
      textTransform: 'none',
      padding: '10px 20px',
      marginTop: '8px'
    };

    // Get title and description from props, fallback to button label if not available
    const firstTitle = (this.props.workflowForm as any).firstButtonTitleLabel || '';
    const firstDescription = (this.props.workflowForm as any).firstButtonDescriptionLabel || 'dawf';
    const secondTitle = (this.props.workflowForm as any).secondButtonTitleLabel || '';
    const secondDescription = (this.props.workflowForm as any).secondButtonDescriptionLabel || '';

    return (
      <div className="d-flex flex-row flex-wrap justify-content-center m-5 repair-in-progress-container">
        {!this.props.workflowForm.hideFirstButton && (
          <div className="repair-progress-card" style={firstCardStyle}>
            <div style={containerIconStyle}>
              <img src={plusSignImage} alt="Icon" style={iconImageStyle} />
            </div>
            <img src={updateCalc} alt="Repair Finished" style={imageStyle} />
            <div style={{ textAlign: 'center' }}>
              <Typography
                variant="h6"
                style={{
                  marginBottom: '12px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  fontSize: '18px',
                  color: isDisabled ? disabledColor : '#606060',
                  minHeight: '35px'
                }}
              >
                {this.translatorService.Tranlate(
                  firstTitle,
                  'Do you need to add other parts or operations?'
                )}
              </Typography>
              {firstDescription && (
                <Typography
                  variant="body2"
                  style={{
                    marginBottom: '20px',
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}
                >
                  {this.translatorService.Tranlate(
                    firstDescription,
                    'Use this option only if you need to send a new offer to the client. You will create a new estimate that the client must approve.'
                  )}
                </Typography>
              )}
            </div>
            <div style={{ marginTop: 'auto', width: '100%', minHeight: '80px' }}>
              <Button
                variant="outlined"
                onClick={this.updateOffer}
                disabled={isDisabled}
                fullWidth
                style={firstButtonStyle}
              >
                {this.translatorService.Tranlate(this.props.workflowForm.firstButtonLabel, '')}
              </Button>
              <div style={{ marginTop: '8px', textAlign: 'center', width: '100%' }}>
                <Typography variant="caption" style={{ color: '#ff9800', fontSize: '12px' }}>
                  {this.translatorService.Tranlate('OPTIONAL', '(Optional)')}
                </Typography>
              </div>
            </div>
          </div>
        )}
        {!this.props.workflowForm.hideSecondButton && (
          <div className="repair-progress-card" style={secondCardStyle}>
            <div style={containerIconStyle}>
              <img src={checkmarkImage} alt="Icon" style={iconImageStyle} />
            </div>
            <img src={repairFinishedImage} alt="Update Calc" style={imageStyle} />
            <div style={{ textAlign: 'center' }}>
              <Typography
                variant="h6"
                style={{
                  marginBottom: '12px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  fontSize: '18px',
                  color: isDisabled ? disabledColor : '#4caf50',
                  minHeight: '35px'
                }}
              >
                {this.translatorService.Tranlate(secondTitle, 'Have you completed the car repair?')}
              </Typography>
              {secondDescription && (
                <Typography
                  variant="body2"
                  style={{
                    marginBottom: '20px',
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}
                >
                  {this.translatorService.Tranlate(
                    secondDescription,
                    'Use this option if you have successfully completed the car repair. You will issue the repair invoice, which the client pays from the mobile app.'
                  )}
                </Typography>
              )}
            </div>
            <div style={{ marginTop: 'auto', width: '100%', minHeight: '80px' }}>
              <Button
                variant="contained"
                onClick={this.repairHasBeenFinished}
                disabled={isDisabled}
                fullWidth
                style={secondButtonStyle}
              >
                {this.translatorService.Tranlate(this.props.workflowForm.secondButtonLabel, '')}
              </Button>
            </div>
          </div>
        )}
        {this.renderRepairServiceInvoice()}
      </div>
    );
  };

  private renderDefaultButtons = () => {
    return (
      <div className="text-center m-2">
        {!this.props.workflowForm.hideFirstButton && (
          <Button
            className="m-2"
            variant="contained"
            color="secondary"
            onClick={this.submitDefaultForm}
            disabled={!this.state.hasRights || this.state.executing}
          >
            {this.translatorService.Tranlate(this.props.workflowForm.firstButtonLabel, '')}
          </Button>
        )}
        {!this.props.workflowForm.hideSecondButton && (
          <Button
            className="m-2"
            variant="contained"
            color="secondary"
            onClick={this.updateOffer}
            disabled={!this.state.hasRights || this.state.executing}
          >
            {this.translatorService.Tranlate(this.props.workflowForm.secondButtonLabel, '')}
          </Button>
        )}
      </div>
    );
  };
}

const mergeProps = (
  stateProps: any,
  dispatchProps: any,
  ownProps: ExternalCaseDetailsCaseRepairInProgressFormProps
) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps
});

export default connect(
  (state: ApplicationState) => ({
    caseSettingsState: state.caseSettings,
    appState: state.app
  }),
  CaseSettingsActionCreators,
  mergeProps
)(withSnackbar(CaseDetailsCaseRepairInProgressForm as any));
